import { BankTransaction } from '@/hooks/useBankTransactions';
import { SystemPayment } from '@/hooks/useSystemPayments';

export interface MatchScore {
  payment: SystemPayment;
  score: number;
  reasons: string[];
}

/**
 * Calcula um score de compatibilidade entre uma transação bancária e pagamentos do sistema.
 * Score ≥ 60 = Sugestão de match
 * Score ≥ 80 = Match altamente provável
 */
export function calculateMatchScores(
  bankTransaction: BankTransaction,
  payments: SystemPayment[]
): MatchScore[] {
  const txDate = new Date(bankTransaction.transaction_date);

  return payments
    .map((payment) => {
      let score = 0;
      const reasons: string[] = [];

      // 1. Valor exato: +50 pontos
      const valueDiff = Math.abs(payment.payment_value - bankTransaction.value);
      if (valueDiff < 0.01) {
        score += 50;
        reasons.push('Valor exato');
      } else {
        // Valor próximo (±5%): pontuação proporcional
        const percentDiff = valueDiff / bankTransaction.value;
        if (percentDiff <= 0.01) {
          score += 35;
          reasons.push('Valor ~igual (centavos)');
        } else if (percentDiff <= 0.05) {
          score += 20;
          reasons.push('Valor similar (±5%)');
        }
      }

      // 2. Data: pontuação por proximidade
      const paymentDate = new Date(payment.payment_date);
      const daysDiff = Math.abs(
        Math.floor((txDate.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24))
      );

      if (daysDiff === 0) {
        score += 30;
        reasons.push('Mesma data');
      } else if (daysDiff === 1) {
        score += 25;
        reasons.push('1 dia de diferença');
      } else if (daysDiff <= 3) {
        score += 20;
        reasons.push(`${daysDiff} dias de diferença`);
      } else if (daysDiff <= 5) {
        score += 10;
        reasons.push(`${daysDiff} dias de diferença`);
      }
      // Mais de 5 dias não pontua em data

      // 3. Nome similar: +15 pontos
      const paymentName = (
        payment.customer_name || payment.supplier_name || ''
      ).toLowerCase();
      const counterpartName = (bankTransaction.counterpart_name || '').toLowerCase();

      if (paymentName && counterpartName) {
        // Verificar se alguma palavra do nome aparece
        const paymentWords = paymentName.split(/\s+/).filter((w) => w.length > 2);
        const counterpartWords = counterpartName.split(/\s+/).filter((w) => w.length > 2);

        const matchingWords = paymentWords.filter((word) =>
          counterpartWords.some((cw) => cw.includes(word) || word.includes(cw))
        );

        if (matchingWords.length >= 2) {
          score += 15;
          reasons.push('Nome compatível');
        } else if (matchingWords.length === 1) {
          score += 8;
          reasons.push('Nome parcialmente compatível');
        }
      }

      // 4. Tipo compatível (credit = receivable, debit = payable): +5 pontos
      const typeMatch =
        (bankTransaction.transaction_type === 'credit' && payment.type === 'receivable') ||
        (bankTransaction.transaction_type === 'debit' && payment.type === 'payable');

      if (typeMatch) {
        score += 5;
        // Não adiciona razão pois é esperado
      } else {
        // Tipo incompatível: desconta pontos
        score -= 20;
      }

      return { payment, score, reasons };
    })
    .filter((m) => m.score > 0) // Remover scores negativos ou zero
    .sort((a, b) => b.score - a.score); // Ordenar por maior score
}

/**
 * Encontra o melhor match para uma transação bancária
 */
export function findBestMatch(
  bankTransaction: BankTransaction,
  payments: SystemPayment[]
): MatchScore | null {
  const scores = calculateMatchScores(bankTransaction, payments);
  return scores.length > 0 ? scores[0] : null;
}

/**
 * Encontra sugestões de match (score >= threshold)
 */
export function findMatchSuggestions(
  bankTransaction: BankTransaction,
  payments: SystemPayment[],
  threshold: number = 60
): MatchScore[] {
  return calculateMatchScores(bankTransaction, payments).filter(
    (m) => m.score >= threshold
  );
}
